import React, { useState, useRef } from "react";
import { Droppable, Draggable, DragDropContext } from "@hello-pangea/dnd";
import drag from "../../../assets/images/drag.svg";
import ph_question from "../../../assets/images/ph_question.svg";
import selected_question from "../../../assets/images/selected_question.svg";
import { Input, Select, Popover, Tooltip, Dropdown } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  MoreOutlined,
  CopyOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import environment from "environment";
import apiClient from "utilities/apiClient";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  getCategoryThemeClass,
} from "../../../constants/categoryConstants";
import {
  STATIC_CLASSIFICATION_METADATA,
  hasClassificationOverride,
  hasPendingClassificationChange,
  isClassificationFeedbackCapable,
  getClassificationFeedbackUnavailableKey,
} from "./classificationFeedback";
import ClassificationFeedbackCapabilityNotice from "./ClassificationFeedbackCapabilityNotice";
import { translateMedicineField } from "utilities/TranslationUtility";
import MoneyInput from "views/components/common/Input/MoneyInput";

const LineItem = (props) => {
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => state.common.currentLanguage);

  const {
    treatments,
    treatmentId,
    handleConfirmationPopup,
    handleOnChangeItem,
    onReorderItems,
    onMoveItemToTreatment,
    onDuplicateItem,
    onMoveItemUpDown,
    onItemHighlightStart,
    onItemHighlightEnd,
    highlightedItemKey,
    highlightedField,
    isEdit,
    selectedPage,
    treatmentIndex,
    extracted_invoice_data,
    classificationMetadata = STATIC_CLASSIFICATION_METADATA,
    classificationErrors = {},
    feedbackStatuses = {},
  } = props;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState(null);
  // R16: per-medicine cache so reopening a popup never refetches (payload unchanged).
  const medicineCacheRef = useRef({});
  // const [prevItem, setPrevItem] = useState();
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [openPopups, setOpenPopups] = useState({});
  const [customVAT, setCustomVAT] = useState({});
  const { id } = useParams();

  const enableItemClassification = environment.enableItemClassification;

  const getItemClassification = (item) => {
    const normalizeValue = (value) => {
      if (typeof value === "string") return value.trim();
      if (value === null || value === undefined) return "";
      return String(value).trim();
    };

    const rawCategory = item?.category ?? item?.Category ?? "";
    const category = normalizeValue(rawCategory);

    const rawExclusionSubcode =
      item?.exclusion_subcode ??
      item?.exclusionSubcode ??
      item?.exclusionSubCode ??
      item?.exclusion ??
      "";
    const exclusionSubcode = normalizeValue(rawExclusionSubcode);

    const rawReasoning = item?.reasoning ?? item?.Reasoning ?? "";
    const reasoning = normalizeValue(rawReasoning);

    const rawKeywords =
      item?.detection_keywords ?? item?.detectionKeywords ?? item?.keywords ?? [];
    const detectionKeywords = Array.isArray(rawKeywords) ? rawKeywords : [];

    const rawConfidence =
      item?.confidence_level ?? item?.confidenceLevel ?? "";
    const confidenceLevel = normalizeValue(rawConfidence).toLowerCase();

    return {
      category,
      exclusionSubcode,
      reasoning,
      detectionKeywords,
      confidenceLevel,
      feedbackOriginalCategory: normalizeValue(item?.feedback_original_category),
      feedbackOriginalExclusionSubcode: normalizeValue(
        item?.feedback_original_exclusion_subcode
      ),
      feedbackChangeReason: normalizeValue(item?.feedback_change_reason),
      feedbackChangeContext: item?.feedback_change_context ?? "",
    };
  };

  const getConfidenceToneClass = (confidenceLevel) => {
    if (confidenceLevel === "medium" || confidenceLevel === "low") {
      return `lineitem-ai-confidence-badge--${confidenceLevel}`;
    }

    return "lineitem-ai-confidence-badge--high";
  };

  const confidenceLabelMap = {
    high: "confidenceHigh",
    medium: "confidenceMedium",
    low: "confidenceLow",
  };

  const categorySelectOptions = classificationMetadata.categories.map((opt) => ({
    value: opt.code ?? opt.value,
    label: `${opt.code ?? opt.value} - ${
      opt.translationKey ? t(opt.translationKey) : opt.label
    }`,
  }));

  const exclusionSelectOptions = classificationMetadata.exclusionSubcodes.map((opt) => ({
    value: opt.code ?? opt.value,
    label: `${opt.code ?? opt.value} - ${
      opt.translationKey ? t(opt.translationKey) : opt.label
    }`,
  }));

  const feedbackReasonOptions = classificationMetadata.reasons.map((opt) => ({
    value: opt.value ?? opt.code,
    label: opt.translationKey ? t(opt.translationKey) : opt.label,
  }));

  const handleCategoryChange = (value, itemKey) => {
    const cat = value ?? "";
    handleOnChangeItem(treatmentId, itemKey, "category", cat);
    if (cat !== "E") {
      handleOnChangeItem(treatmentId, itemKey, "exclusion_subcode", "");
    }
  };

  const handleExclusionSubcodeChange = (value, itemKey) => {
    handleOnChangeItem(treatmentId, itemKey, "exclusion_subcode", value ?? "");
  };

  const handleFeedbackReasonChange = (value, itemKey) => {
    handleOnChangeItem(treatmentId, itemKey, "feedback_change_reason", value ?? "");
  };

  const handleFeedbackContextChange = (event, itemKey) => {
    handleOnChangeItem(
      treatmentId,
      itemKey,
      "feedback_change_context",
      event.target.value
    );
  };

  const renderItemClassification = (item) => {
    if (!enableItemClassification) return null;

    const itemName = (item?.["Item Name"] ?? "").toString().trim();
    const {
      category,
      exclusionSubcode,
      reasoning,
      detectionKeywords,
      confidenceLevel,
      feedbackOriginalCategory,
      feedbackOriginalExclusionSubcode,
      feedbackChangeReason,
      feedbackChangeContext,
    } = getItemClassification(item);

    const normalizedKeywords = detectionKeywords
      .filter((keyword) => typeof keyword === "string" && keyword.trim().length > 0)
      .map((keyword) => keyword.trim());

    const hasAnyClassification =
      category.length > 0 ||
      exclusionSubcode.length > 0 ||
      reasoning.length > 0 ||
      normalizedKeywords.length > 0;

    if (!itemName && !hasAnyClassification) return null;

    const visibleKeywords = normalizedKeywords.slice(0, 3);
    const hiddenKeywords = normalizedKeywords.slice(3);
    const isClassificationChanged = hasClassificationOverride(item);
    const isPendingChange = hasPendingClassificationChange(item);
    const confidenceLabelKey = confidenceLabelMap[confidenceLevel] || "";
    const itemErrors = classificationErrors[item.key] || {};
    const isFeedbackCapable = isClassificationFeedbackCapable(item);
    const correctionControlsDisabled =
      isEdit || !isFeedbackCapable || Boolean(itemErrors.identity);
    const feedbackStatus = feedbackStatuses[item.key];
    const errorId = (field) => `classification-${item.key}-${field}-error`;
    const feedbackState =
      feedbackStatus?.state ||
      (isPendingChange ? "draft" : isClassificationChanged ? "saved" : "");
    const statusLabelKey =
      feedbackState === "pending"
        ? "feedbackStatusPending"
        : feedbackState === "saved"
        ? "feedbackStatusSaved"
        : feedbackState === "error"
        ? feedbackStatus.messageKey || "feedbackStatusError"
        : feedbackState === "draft"
        ? "feedbackStatusDraft"
        : "";

    return (
      <div className={`lineitem-ai ${getCategoryThemeClass(category)}`}>
        <div className="lineitem-ai-top">
          <div className="lineitem-ai-categories">
            <div
              className="lineitem-ai-field"
              data-feedback-error={Boolean(
                itemErrors.category || (category !== "E" && itemErrors.subcode)
              )}
            >
              <span className="lineitem-ai-field-label">{t("category")}</span>
              <Select
                value={category || undefined}
                placeholder={t("unclassified")}
                onChange={(val) => handleCategoryChange(val, item.key)}
                disabled={correctionControlsDisabled}
                size="small"
                popupMatchSelectWidth={false}
                className="lineitem-ai-select"
                options={categorySelectOptions}
                allowClear
                status={itemErrors.category ? "error" : undefined}
                aria-label={t("category")}
                aria-invalid={Boolean(itemErrors.category)}
                aria-describedby={
                  itemErrors.category ? errorId("category") : undefined
                }
              />
              {itemErrors.category && (
                <span
                  id={errorId("category")}
                  className="lineitem-ai-field-error"
                  role="alert"
                >
                  {t(itemErrors.category)}
                </span>
              )}
              {category !== "E" && itemErrors.subcode && (
                <span
                  id={errorId("subcode")}
                  className="lineitem-ai-field-error"
                  role="alert"
                >
                  {t(itemErrors.subcode)}
                </span>
              )}
            </div>

            {category === "E" ? (
              <div
                className="lineitem-ai-field lineitem-ai-field--narrow"
                data-feedback-error={Boolean(itemErrors.subcode)}
              >
                <span className="lineitem-ai-field-label">{t("exclusionSubcode")}</span>
                <Select
                  value={exclusionSubcode || undefined}
                  placeholder="E-..."
                  onChange={(val) => handleExclusionSubcodeChange(val, item.key)}
                  disabled={correctionControlsDisabled}
                  size="small"
                  popupMatchSelectWidth={false}
                  className="lineitem-ai-select"
                  options={exclusionSelectOptions}
                  allowClear
                  status={itemErrors.subcode ? "error" : undefined}
                  aria-label={t("exclusionSubcode")}
                  aria-invalid={Boolean(itemErrors.subcode)}
                  aria-describedby={
                    itemErrors.subcode ? errorId("subcode") : undefined
                  }
                />
                {itemErrors.subcode && (
                  <span
                    id={errorId("subcode")}
                    className="lineitem-ai-field-error"
                    role="alert"
                  >
                    {t(itemErrors.subcode)}
                  </span>
                )}
              </div>
            ) : null}

            {confidenceLabelKey ? (
              <Tooltip title={t(confidenceLabelKey)}>
                <div
                  className={`lineitem-ai-confidence-badge ${getConfidenceToneClass(confidenceLevel)}`}
                >
                  <span
                    className={`lineitem-ai-confidence lineitem-ai-confidence--${confidenceLevel}`}
                  />
                  <span className="lineitem-ai-confidence-text">
                    {t(confidenceLabelKey)}
                  </span>
                </div>
              </Tooltip>
            ) : null}
          </div>

          {visibleKeywords.length > 0 ? (
            <div className="lineitem-ai-keywords">
              {visibleKeywords.map((keyword) => (
                <span key={keyword} className="lineitem-ai-keyword">
                  {keyword}
                </span>
              ))}
              {hiddenKeywords.length > 0 ? (
                <Tooltip title={hiddenKeywords.join(", ")}>
                  <span className="lineitem-ai-keyword lineitem-ai-keyword--more">
                    +{hiddenKeywords.length}
                  </span>
                </Tooltip>
              ) : null}
            </div>
          ) : null}
        </div>

        {reasoning ? (
          <Tooltip title={reasoning}>
            <div className="lineitem-ai-reasoning">
              <span className="lineitem-ai-reasoning-label">{t("reasoning")}:</span>
              <span className="lineitem-ai-reasoning-text">{reasoning}</span>
            </div>
          </Tooltip>
        ) : null}

        {isClassificationChanged ? (
          <div className="lineitem-ai-comparison">
            <span>
              {t("feedbackOriginal")}:{" "}
              <strong>
                {feedbackOriginalCategory || t("unclassified")}
                {feedbackOriginalExclusionSubcode
                  ? ` / ${feedbackOriginalExclusionSubcode}`
                  : ""}
              </strong>
            </span>
            <span aria-hidden="true">→</span>
            <span>
              {t("feedbackCorrected")}:{" "}
              <strong>
                {category || t("unclassified")}
                {exclusionSubcode ? ` / ${exclusionSubcode}` : ""}
              </strong>
            </span>
          </div>
        ) : null}

        {statusLabelKey ? (
          <div
            className={`lineitem-ai-feedback-status lineitem-ai-feedback-status--${
              feedbackState
            }`}
            role={feedbackState === "error" ? "alert" : "status"}
            aria-live="polite"
          >
            {t(statusLabelKey)}
          </div>
        ) : null}

        {itemErrors.identity || !isFeedbackCapable ? (
          <ClassificationFeedbackCapabilityNotice
            message={t(
              itemErrors.identity
                ? itemErrors.identity
                : getClassificationFeedbackUnavailableKey(item)
            )}
            isError={Boolean(itemErrors.identity)}
          />
        ) : null}

        {isPendingChange ? (
          <div className="lineitem-ai-feedback">
            <div
              className="lineitem-ai-field lineitem-ai-field--feedback"
              data-feedback-error={Boolean(itemErrors.reason)}
            >
              <span className="lineitem-ai-field-label lineitem-ai-field-label--required">
                {t("reasonForChange")} ({t("required")})
              </span>
              <Select
                value={feedbackChangeReason || undefined}
                placeholder={t("reasonForChangePlaceholder")}
                onChange={(val) => handleFeedbackReasonChange(val, item.key)}
                disabled={correctionControlsDisabled}
                size="small"
                popupMatchSelectWidth={false}
                className="lineitem-ai-select"
                options={feedbackReasonOptions}
                allowClear
                status={itemErrors.reason ? "error" : undefined}
                aria-label={t("reasonForChange")}
                aria-required="true"
                aria-invalid={Boolean(itemErrors.reason)}
                aria-describedby={
                  itemErrors.reason ? errorId("reason") : undefined
                }
              />
              {itemErrors.reason && (
                <span
                  id={errorId("reason")}
                  className="lineitem-ai-field-error"
                  role="alert"
                >
                  {t(itemErrors.reason)}
                </span>
              )}
            </div>

            {feedbackChangeReason ? (
              <div
                className="lineitem-ai-field lineitem-ai-field--feedback lineitem-ai-field--feedback-wide"
                data-feedback-error={Boolean(itemErrors.context)}
              >
                <span className="lineitem-ai-field-label">
                  {t("changeContext")}
                  {feedbackChangeReason === "other"
                    ? ` (${t("required")})`
                    : ""}
                </span>
                <Input.TextArea
                  value={feedbackChangeContext}
                  placeholder={t("changeContextPlaceholder")}
                  onChange={(event) => handleFeedbackContextChange(event, item.key)}
                  disabled={correctionControlsDisabled}
                  className="lineitem-ai-feedback-input"
                  rows={2}
                  maxLength={classificationMetadata.contextMaxLength}
                  showCount
                  status={itemErrors.context ? "error" : undefined}
                  aria-label={t("changeContext")}
                  aria-required={feedbackChangeReason === "other"}
                  aria-invalid={Boolean(itemErrors.context)}
                  aria-describedby={
                    itemErrors.context ? errorId("context") : undefined
                  }
                />
                {itemErrors.context && (
                  <span
                    id={errorId("context")}
                    className="lineitem-ai-field-error"
                    role="alert"
                  >
                    {t(itemErrors.context, {
                      max: classificationMetadata.contextMaxLength,
                    })}
                  </span>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const handleOnChangeVAT = (e, itemKey) => {
    handleOnChangeItem(treatmentId, itemKey, "VAT", e);
    setCustomVAT((prev) => ({
      ...prev,
      [itemKey]: e,
    }));
  };

  const handleCustomInputChange = (e, itemKey) => {
    const value = e.target.value.replace("%", "").trim();
    setCustomVAT((prev) => ({
      ...prev,
      [itemKey]: value,
    }));
    handleOnChangeVAT(value, itemKey);
  };

  const Popup = ({ itemId }) => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "94px",
            width: "100%",
          }}
        >
          <div className="">
            <div className="popover-loader"></div>
            <div className="popover-loader-text">{t("loading")}</div>
          </div>
        </div>
      );
    }
    if (!data || !description) {
      return null;
    }
    return (
      <div className="col-md-12 text-start px-0 mt-3 position-relative">
        <div className="history-header">
          <h1 className="fs16 text-blue-a poppins-medium">{data}</h1>
          <button
            type="button"
            className="btn-close position-absolute top-0 end-0 m-3"
            aria-label={t("close")}
            onClick={() => handleClosePopup(itemId)}
            style={{
              filter: "brightness(80%)",
            }}
          ></button>
          <h3 className="fs12 cell-gray my-2 pb-1"> {t("medicineDescription")}</h3>
          <div
            style={{ borderBottom: "1px solid #EBEEF2", marginBottom: "10px" }}
          ></div>
          <div className="row">
            {Object.entries(description.medicine_description).map(([key, value]) => (
                <div className="col-sm-6" key={key}>
                <p className="fs12 dark-text poppins-medium mb-2">
                  {translateMedicineField(t, key)}
                </p>
                  {Array.isArray(value) ? (
                    <ul>
                      {value.map((item, index) => (
                        <li className="fs11 medicine-description-popup-values" key={index}>
                          - {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="fs11 medicine-description-popup-values">- {value}</p>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  const fetchMedicineInfo = async (name, itemId) => {
    const cacheKey = `${name}__${currentLanguage}`;
    // Serve from cache when available — no refetch, no loading flicker.
    if (medicineCacheRef.current[cacheKey]) {
      setDescription(medicineCacheRef.current[cacheKey]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const payload = {
        id: id,
        extractedInvoiceData: extracted_invoice_data,
        pageNo: selectedPage - 1,
        treatmentOrder: treatmentIndex,
        itemOrder: itemId,
        medicineName: name,
        language: currentLanguage
      };
      // R21: apiClient centralizes auth headers + 401/force-logout.
      const response = await apiClient.post(`/document/medicine`, payload);
      setLoading(false);
      if (!response || response?.result?.error) {
        throw new Error(
          "Invalid response data or missing medicine description"
        );
      }
      let result = {
        medicine_description: {},
      };
      const medicineDescription = response?.result?.medicine_description;
      result.medicine_description["purpose"] =
        medicineDescription.purpose;
      result.medicine_description["usedFor"] =
        medicineDescription.used_for;
      result.medicine_description["petTypes"] =
        medicineDescription.pet_types;
      result.medicine_description["activeIngredients"] =
        medicineDescription.active_ingredients;

      if (enableItemClassification) {
        result.medicine_description["category"] =
          medicineDescription.category || "";
        result.medicine_description["typeOfDamage"] =
          medicineDescription.type_of_damage;
        result.medicine_description["causeOfDamage"] =
          medicineDescription.cause_of_damage;
        result.medicine_description["reasoning"] =
          medicineDescription.reasoning || "";
        result.medicine_description["detection_keywords"] =
          medicineDescription.detection_keywords || [];
        
        // Extract item classification data from extracted_invoice_data
        const currentPageData = extracted_invoice_data[selectedPage - 1];
        if (currentPageData && currentPageData.treatments && currentPageData.treatments[treatmentIndex]) {
          const currentTreatment = currentPageData.treatments[treatmentIndex];
          if (currentTreatment.Items && currentTreatment.Items[itemId]) {
            const itemData = currentTreatment.Items[itemId];
            result.medicine_description["category"] =
              result.medicine_description["category"] ||
              itemData.category ||
              "";
            result.medicine_description["reasoning"] =
              result.medicine_description["reasoning"] ||
              itemData.reasoning ||
              "";
            if (!result.medicine_description["detection_keywords"].length) {
              result.medicine_description["detection_keywords"] =
                itemData.detection_keywords || [];
            }
          }
        }
      }

      setDescription(result);
      medicineCacheRef.current[cacheKey] = result;
    } catch (error) {
      console.error("Error fetching medicine information:", error);
      setLoading(false);
    }
  };

  const reorderItems = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }
    // Reorder items
    let reordered = reorderItems(
      treatments.Items,
      source.index,
      destination.index
    );
    // Update item_order based on new order
    reordered = reordered.map((item, idx) => ({
      ...item,
      item_order: idx + 1,
    }));
    // delegate the state update to the parent (no prop mutation)
    if (onReorderItems) {
      onReorderItems(treatmentId, reordered);
    }
  };

  const handleClosePopup = (itemId) => {
    setSelectedMedicine(null);
    setOpenPopups((prevState) => ({
      ...prevState,
      [itemId]: false,
    }));
  };

  const handleImageClick = (itemId, itemName) => {
    setSelectedMedicine(itemId);
    setOpenPopups((prevState) => ({
      ...prevState,
      [itemId]: true,
    }));
    setData(itemName);
    fetchMedicineInfo(itemName, itemId);
  };

  // R18: per-item action menu (duplicate, move-to-treatment submenu, delete).
  const buildItemMenu = () => {
    const treatmentsOnPage =
      extracted_invoice_data?.[selectedPage - 1]?.treatments || [];
    const otherTreatments = treatmentsOnPage
      .map((tr, idx) => ({ tr, idx }))
      .filter(({ tr }) => tr.key !== treatmentId);

    const menuItems = [
      { key: "duplicate", label: t("duplicateItem"), icon: <CopyOutlined /> },
    ];

    if (otherTreatments.length) {
      menuItems.push({
        key: "moveTo",
        label: t("moveToTreatment"),
        children: otherTreatments.map(({ tr, idx }) => ({
          key: `moveTo:${tr.key}`,
          label: `${t("treatment")} ${idx + 1}`,
        })),
      });
    }

    menuItems.push({ type: "divider" });
    menuItems.push({
      key: "delete",
      label: t("deleteItem"),
      icon: <DeleteOutlined />,
      danger: true,
    });
    return menuItems;
  };

  const handleItemMenuClick = (item) => ({ key }) => {
    if (key === "duplicate") {
      onDuplicateItem && onDuplicateItem(treatmentId, item.key);
    } else if (key === "delete") {
      handleConfirmationPopup("line-item", treatmentId, item.key);
    } else if (key.startsWith("moveTo:")) {
      const targetKey = key.slice("moveTo:".length);
      onMoveItemToTreatment && onMoveItemToTreatment(treatmentId, item.key, targetKey);
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={String(treatmentId)} type="line-item">
          {(provided) => (
            <div ref={provided.innerRef}>
              {treatments.Items.map((item, index) => (
                <Draggable
                  key={`${item.key}`}
                  draggableId={`${item.key}`}
                  index={index}
                  isDragDisabled={isEdit}
                >
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <div
                        className={`lineitem-block ${
                          index % 2 === 1 ? "lineitem-block--alt" : ""
                        } ${
                          highlightedItemKey === item.key
                            ? "lineitem-block--preview-active"
                            : ""
                        }`.trim()}
                        onPointerEnter={() =>
                          onItemHighlightStart?.(item, "Item Name")
                        }
                        onPointerLeave={() => onItemHighlightEnd?.(item.key)}
                        onFocusCapture={(event) => {
                          const field =
                            event.target?.getAttribute?.("data-preview-field") ||
                            event.target?.name ||
                            "Item Name";
                          onItemHighlightStart?.(item, field);
                        }}
                        onBlurCapture={(event) => {
                          if (!event.currentTarget.contains(event.relatedTarget)) {
                            onItemHighlightEnd?.(item.key);
                          }
                        }}
                      >
                        <div className="lineitem-block-index" aria-hidden="true">
                          {index + 1}
                        </div>
                      <div
                        className="lineitem-block-main"
                        key={item.item_order}
                      >
                          <div className="lineitem-name">
                            <span className="lineitem-drag" {...provided.dragHandleProps}>
                              <img
                                src={drag}
                                alt={t("drag")}
                              />
                            </span>
                            <div className="lineitem-name-field">
                              <Input
                                name="Item Name"
                                placeholder={t("itemName")}
                                value={item["Item Name"]}
                                data-preview-field="Item Name"
                                data-preview-item-key={item.key}
                                data-preview-treatment-key={treatmentId}
                                onChange={(e) =>
                                  handleOnChangeItem(
                                    treatmentId,
                                    item.key,
                                    e.target.name,
                                    e.target.value
                                  )
                                }
                                className={`${
                                  item["Item Name"]?.trim() === "" ||
                                  item["Item Name"] === null
                                    ? "bg-invoiceorange"
                                    : ""
                                } ${
                                  item.isNew ? "treatment-addnew-row" : ""
                                } ${
                                  enableItemClassification &&
                                  (item?.confidence_level ??
                                    item?.confidenceLevel ??
                                    "")
                                    .toString()
                                    .trim()
                                    .toLowerCase() === "low"
                                    ? "attention-lowconf"
                                    : ""
                                } ${
                                  highlightedItemKey === item.key &&
                                  highlightedField === "Item Name"
                                    ? "attention-focus-ring"
                                    : ""
                                }`.trim()}
                                disabled={isEdit}
                              />
                              {environment.APP_ENVIRONMENT !== "production" ? (
                                <Popover
                                  content={<Popup itemId={index} />}
                                  trigger="click"
                                  open={openPopups[index]}
                                  overlayStyle={{ width: 320, maxWidth: "90vw" }}
                                >
                                  <Tooltip
                                    title={t("medicine_info")}
                                    placement="bottom"
                                  >
                                    <button
                                      type="button"
                                      className="circle-btn medicine-info-btn"
                                      aria-label={t("medicine_info")}
                                      onClick={() =>
                                        handleImageClick(
                                          index,
                                          item["Item Name"]
                                        )
                                      }
                                    >
                                      <img
                                        className="medicine-info cursor-pointer"
                                        src={
                                          selectedMedicine === index
                                            ? selected_question
                                            : ph_question
                                        }
                                        alt=""
                                      />
                                    </button>
                                  </Tooltip>
                                </Popover>
                              ) : null}
                            </div>
                          </div>
                                <div className="lineitem-qty">
                                  <MoneyInput
                                    name={"Item Quantity"}
                                    placeholder={t("quantity")}
                                    value={item["Item Quantity"]}
                                    data-preview-field="Item Quantity"
                                    data-preview-item-key={item.key}
                                    data-preview-treatment-key={treatmentId}
                                    onChange={(e) =>
                                      handleOnChangeItem(
                                        treatmentId,
                                        item.key,
                                        e.target.name,
                                        e.target.value
                                      )
                                    }
                                    className={`${
                                      item["Item Quantity"]?.trim() === "" ||
                                      item["Item Quantity"] === null
                                        ? "bg-invoiceorange"
                                        : ""
                                    } ${
                                      item.isNew ? "treatment-addnew-row" : ""
                                    } ${
                                      highlightedItemKey === item.key &&
                                      highlightedField === "Item Quantity"
                                        ? "attention-focus-ring"
                                        : ""
                                    }`.trim()}
                                    disabled={isEdit}
                                  />
                                </div>
                                <div className="lineitem-price">
                                  <MoneyInput
                                    name="Item Price"
                                    placeholder={t("prize")}
                                    value={item["Item Price"]}
                                    data-preview-field="Item Price"
                                    data-preview-item-key={item.key}
                                    data-preview-treatment-key={treatmentId}
                                    onChange={(e) =>
                                      handleOnChangeItem(
                                        treatmentId,
                                        item.key,
                                        e.target.name,
                                        e.target.value
                                      )
                                    }
                                    className={`${
                                      item["Item Price"]?.toString()?.trim() ===
                                        "" || item["Item Price"] === null
                                        ? "bg-invoiceorange"
                                        : ""
                                    } ${
                                      item?.isNew ? "treatment-addnew-row" : ""
                                    } ${
                                      highlightedItemKey === item.key &&
                                      highlightedField === "Item Price"
                                        ? "attention-focus-ring"
                                        : ""
                                    }`?.trim()}
                                    disabled={isEdit}
                                  />
                                </div>
                                <div className="lineitem-vat vat-select-option">
                                  <Select
                                    value={item["VAT"] ?? ""}
                                    name={"VAT"}
                                    placeholder={t("VAT")}
                                    onChange={(e) =>
                                      handleOnChangeVAT(e, item.key)
                                    }
                                    className={`${
                                      item["VAT"]?.trim() === "" ||
                                      item["VAT"] === null
                                        ? "bg-invoiceorange"
                                        : ""
                                    } ${
                                      item.isNew
                                        ? "treatment-addnew-select"
                                        : ""
                                    }`.trim()}
                                    dropdownRender={(menu) => (
                                      <>
                                        {menu}
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "8px",
                                          }}
                                        >
                                        <Tooltip title={t("customVAT")} placement="bottom">
                                          <MoneyInput
                                            style={{ flex: "auto" }}
                                            value={customVAT[item.key]}
                                            onChange={(e) =>
                                              handleCustomInputChange(
                                                e,
                                                item.key
                                              )
                                            }
                                            placeholder={t("customVAT")}
                                            disabled={isEdit}
                                          />
                                          </Tooltip>
                                        </div>
                                      </>
                                    )}
                                    options={[
                                      {
                                        value: "",
                                        label: t("none"),
                                      },
                                      {
                                        value: "2.5",
                                        label: "2.5%",
                                      },
                                      {
                                        value: "2.6",
                                        label: "2.6%",
                                      },
                                      {
                                        value: "7.7",
                                        label: "7.7%",
                                      },
                                      {
                                        value: "8.1",
                                        label: "8.1%",
                                      },
                                    ]}
                                    disabled={isEdit}
                                  />
                                </div>
                            <div className="lineitem-actions">
                              <Tooltip title={t("moveUp")} placement="bottom">
                                <button
                                  type="button"
                                  className="lineitem-action-btn"
                                  aria-label={t("moveUp")}
                                  disabled={isEdit || index === 0}
                                  onClick={() =>
                                    onMoveItemUpDown &&
                                    onMoveItemUpDown(treatmentId, item.key, -1)
                                  }
                                >
                                  <ArrowUpOutlined />
                                </button>
                              </Tooltip>
                              <Tooltip title={t("moveDown")} placement="bottom">
                                <button
                                  type="button"
                                  className="lineitem-action-btn"
                                  aria-label={t("moveDown")}
                                  disabled={
                                    isEdit || index === treatments.Items.length - 1
                                  }
                                  onClick={() =>
                                    onMoveItemUpDown &&
                                    onMoveItemUpDown(treatmentId, item.key, 1)
                                  }
                                >
                                  <ArrowDownOutlined />
                                </button>
                              </Tooltip>
                              <Dropdown
                                menu={{
                                  items: buildItemMenu(),
                                  onClick: handleItemMenuClick(item),
                                }}
                                trigger={["click"]}
                                disabled={isEdit}
                                overlayClassName="app-dropdown"
                                placement="bottomRight"
                              >
                                <button
                                  type="button"
                                  className="lineitem-action-btn"
                                  aria-label={t("moreActions")}
                                  disabled={isEdit}
                                >
                                  <MoreOutlined />
                                </button>
                              </Dropdown>
                            </div>
                      </div>
                      {renderItemClassification(item)}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default LineItem;
